<template>
  <div
    class="videoAdvantages videoAdvantages1 videoAdvantages--1 lp-section width-expand padding-2xl-vertical l:padding-max-vertical"
    :class="`bg-${backgroundColor} c-${featureTextColor}`"
    :id="`VideoAdvantages1-${pageUid}`">
    <div class="container margin-auto-inline">
      <div
        class="title-block text-center margin-auto-inline"
        v-if="isNonEmptyString(title)">
        <h2 class="margin-remove" :class="`c-${titleColor}`">
          {{ title }}
        </h2>
      </div>
      <div
        class="main-content flex-column gap-2xl l:flex-stretch l:flex-between l:flex-row">
        <div
          class="advantages-block gap-xl l:gap-2xl flex-column l:flex-center width-expand">
          <div
            class="advantage flex gap-m"
            v-for="(advantage, advantageIndex) in advantages">
            <div
              v-if="decorationType === 'lines'"
              class="line-decoration"
              :class="`bg-${advantageDecorationColor}`"
              style="width: 2px"></div>
            <div class="advantage-content">
              <div
                class="advantage-title flex gap-s margin-s-bottom"
                :class="{ 'flex-bottom': decorationType === 'numbers' }">
                <icon
                  v-if="decorationType === 'checkIcons'"
                  :icon="'check'"
                  :size="1.5"
                  :theme="advantageDecorationColor"></icon>
                <span
                  class="text-2xl"
                  v-if="decorationType === 'numbers'"
                  :class="`c-${advantageDecorationColor}`">
                  {{ +advantageIndex + 1 }}.
                </span>
                <h3
                  :class="`c-${advantageTitleColor}`"
                  style="font-weight: 600"
                  class="text-xl margin-remove">
                  {{ advantage.title }}
                </h3>
              </div>
              <p
                :class="`c-${advantageTextColor}`"
                v-html="nl2Br(advantage.text)"></p>
            </div>
          </div>
          <div
            class="button-wrapper margin-l-top l:margin-xl-top l:flex-start"
            v-if="button.show">
            <form-button
              v-bind="button"
              :class="{ 'width-expand': isSmallerThanL }"
              @click="
                track('videoAdvantages1|button|click', {
                  value: button.text,
                }).then(button.navigationHandler)
              "></form-button>
          </div>
        </div>
        <div class="video-block flex-column l:flex-center width-expand">
          <div
            style="aspect-ratio: 16 / 9"
            class="video-ratio-container border-radius-s margin-auto-inline relative z-level-2 border-radius">
            <div
              v-if="videoCoverImage.show && !showVideoIframe"
              @click="
                track('video1|coverImage|click', { value: title }).then(
                  showVideo
                )
              "
              class="cursor-pointer video-cover relative bg-danger width-expand relative z-level-2 border-radius background-cover relative"
              :style="`background-image: url('${videoCoverImage.finalSrc}')`"
              style="height: 100%">
              <div class="absolute-center font-max">
                <icon-halo
                  :size="3"
                  style="padding-left: 4px"
                  icon="mc-play"
                  :halo-theme="titleColor"
                  theme="bg"></icon-halo>
              </div>
            </div>
            <iframe
              v-if="showVideoIframe && ! $s.isCrawler"
              class="relative z-level-2 border-radius"
              style="border: none"
              loading="lazy"
              width="100%"
              height="100%"
              :src="`https://www.youtube.com/embed/${videoId}?autoplay=${
                videoAutoPlay ? 'true' : 'false'
              }&rel=0`"
              title="YouTube video player"
              allow="accelerometer; clipboard-write; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen></iframe>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useUiElements from "@/client/extensions/composition/useUiElements";
import lpSectionMixin from "@/client/applications/meitavlp/extensions/mixins/lpSection";
import { inject } from "vue";
import useDom from "@/client/extensions/composition/useDom";

export default {
  mixins: [lpSectionMixin],

  setup(props) {
    let pageUid = inject("pageUid");
    let { isSmallerThanL } = useDom();

    let schema = {
      title: "",
      decorationType: "",
      advantages: [
        {
          mainTitle: "",
          title: "",
          text: "",
        },
      ],
      button: {},
      videoId: "",
      videoCoverImage: {},
      backgroundColor: "",
      titleColor: "",
      advantageTitleColor: "",
      advantageDecorationColor: "",
      advantageTextColor: "",
    };
    let overloadedProps = useUiElements({ input: props.sectionData }).props;
    return {
      ...schema,
      ...overloadedProps,
      allData: overloadedProps,
      pageUid,
      trackEvent: inject("trackEvent"),
      track: inject("track"),
      triggerSectionsEvent: inject("triggerSectionsEvent"),
      isSmallerThanL,
    };
  },
  data: function () {
    // if have cover - default off
    let initialShowVideoIframeState = !this.videoCoverImage.show;
    return {
      // if we show initially - no autoplay
      videoAutoPlay: !initialShowVideoIframeState,
      showVideoIframe: initialShowVideoIframeState,
    };
  },
  computed: {
    hasCover() {
      return this.videoCoverImage.show;
    },
  },
  methods: {
    showVideo() {
      this.showVideoIframe = true;
    },
    // iframe tracking
    blurHandler() {
      setTimeout(() => {
        if (document.activeElement.tagName === "IFRAME") {
          try {
            this.track("videoAdvantages1|video|click", {
              value: this.videoId,
            }).then(() => {});
          } catch (e) {}
        }
      });
    },
  },
  mounted() {
    try {
      window.focus();
      window.addEventListener("blur", this.blurHandler, { once: true });
    } catch (e) {}
  },
  beforeUnmount() {
    window.removeEventListener("blur", this.blurHandler);
  },
};
</script>

<style scoped lang="scss">
.videoAdvantages1 {
  .title-block {
    margin-bottom: calc(var(--margin-max) * 0.7);
    font-weight: 600;

    @media (max-width: 1024px) {
      margin-bottom: calc(var(--margin-max) * 0.6);
    }
  }

  .main-content {
    .advantages-block {
      max-width: 460px;

      @media (max-width: 1024px) {
        order: 2;
        margin-inline: auto;
        width: 100%;
      }
    }

    .video-block {
      @media (min-width: 1024px) {
        min-width: 500px;
        max-width: 700px;
        flex: 100%;
        width: 100%;
      }

      @media (max-width: 1024px) {
        order: 1;
        max-width: 460px;
        margin-inline: auto;
      }
    }

    .video-ratio-container {
      width: 100%;
    }
  }
}
</style>
